/* eslint-disable react-portal/no-window-location-assign */
/* eslint-disable react-portal/no-react-router-use-navigate */
import { createContext, useContext, useEffect, useMemo } from 'react';
import { node } from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import useSession from '../../stores/SessionStore';
import useEnvironment from '../../stores/EnvironmentStore';
import SessionService from '../../services/SessionService';
import { SSO_LOGIN_REDIRECT } from '../auth/AuthConstants';
import usePostLoginRedirect from '../auth/UsePostLoginRedirect';
import useIsPrinting from '../../utilities/PrintUtilities';
import LicenseAlertProvider from './ServerStatusProvider';

const AppContext = createContext({});

export function useAppContext() {
    return useContext(AppContext);
}

function useAppContextValue() {
    const [environment, environmentActions] = useEnvironment();

    // Load the global environment for the application.
    useEffect(() => {
        environmentActions.load();
    }, [environmentActions]);

    // This makes the global environment available via the AppContext so that
    // it can be accessed in PLM pages that have a local PLM environment.
    return useMemo(
        () => ({
            environment,
        }),
        [environment]
    );
}

export function useAppEnvironment() {
    const { environment } = useAppContext();
    return environment;
}

function isPathToTrack(path) {
    if (!path || path.length < 2) {
        return false;
    }

    return (
        path.indexOf('/login') < 0 &&
        path.indexOf('/logout') < 0 &&
        path.indexOf('/fonts') < 0 &&
        path.indexOf('.gif') < 0 &&
        path.indexOf('.png') < 0 &&
        path.indexOf('gotoPulse') < 0 &&
        path.indexOf('landing')
    );
}

export default function AppSessionProvider(props) {
    const { children } = props;
    const [session, sessionActions] = useSession();
    const appContextValue = useAppContextValue();
    const { environment } = appContextValue;
    const location = useLocation();
    const navigate = useNavigate();
    const { saveCurrentLocation } = usePostLoginRedirect();
    const isPrinting = useIsPrinting();

    // Once the environment is loaded, kick off the session refresh.
    useEffect(() => {
        if (!environment.initialized) return;

        sessionActions.refresh(environment.environment, !isPrinting);
    }, [environment, isPrinting, sessionActions]);

    // Once the session is loaded, check if the user is authorized.
    useEffect(() => {
        // wait for session initialization
        if (!session?.initialized) {
            if (isPathToTrack(location.pathname)) {
                // Save the URL in session storage so the login page knows where to go after login.
                saveCurrentLocation();
            }

            return;
        }

        // Require log in if the session isn't authorized.
        if (!session.authorized) {
            if (isPathToTrack(location.pathname)) {
                // Save the URL in session storage so the login page knows where to go after login.
                saveCurrentLocation();
            }
            window.location.assign(SSO_LOGIN_REDIRECT);
        } else if (session.userInfo.user.changePassword === true) {
            saveCurrentLocation();
            navigate('/login/changePassword');
        }
    }, [location.pathname, navigate, session, saveCurrentLocation]);

    // Update the user last active time whenenver the location changes.
    useEffect(() => {
        SessionService.updateLastActive();
    }, [location]);

    // do not continue further into the app without a valid session
    if (!session.initialized || !session.authorized) return null;

    return (
        <AppContext.Provider value={appContextValue}>
            <LicenseAlertProvider />
            {children}
        </AppContext.Provider>
    );
}

AppSessionProvider.propTypes = {
    children: node,
};

AppSessionProvider.defaultProps = {
    children: null,
};
